import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Send, X, Paperclip, Trash2, Loader2, Mail, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/cn";
import { useActiveDraft } from "@/hooks/use-active-draft";
import {
  getBundle, addBundleRecipients, removeBundleRecipient,
  removeBundleItem, sendBundle, updateBundle, fileKeys,
  type GateMode,
} from "@/lib/files-api";

/**
 * Floating chip that follows the user across the app while they have a draft
 * share bundle. Mirrors the spec's ActiveDraftChip behavior:
 *   - hidden when activeDraft === null
 *   - collapsed: pill with file count + bundle name
 *   - expanded: popover with files, recipient list, gate-mode toggle, send
 *   - cmd/ctrl+shift+S toggles the popover
 */
export function ActiveDraftChip() {
  const { sessionId } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { draft, clearDraft, refresh } = useActiveDraft();
  const [open, setOpen] = useState(false);
  const [recipEmail, setRecipEmail] = useState("");
  const [recipName, setRecipName] = useState("");

  // Detail when popover open
  const detailQ = useQuery({
    queryKey: [...fileKeys.bundle(draft?.bundleId ?? ""), sessionId],
    queryFn: () => getBundle(draft!.bundleId, sessionId),
    enabled: !!draft?.bundleId && open && !!sessionId,
  });
  const detail = detailQ.data;

  // Hotkey: cmd/ctrl+shift+S toggles the chip
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === "s" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (draft) setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft]);

  const addRecipient = useMutation({
    mutationFn: (input: { email: string; name?: string | null }) =>
      addBundleRecipients(draft!.bundleId, [input], sessionId),
    onSuccess: () => {
      setRecipEmail(""); setRecipName("");
      qc.invalidateQueries({ queryKey: fileKeys.bundle(draft!.bundleId) });
    },
    onError: (e: Error) => toast({ title: "Couldn't add recipient", description: e.message, variant: "destructive" }),
  });

  const removeRecipient = useMutation({
    mutationFn: (recipientId: string) => removeBundleRecipient(draft!.bundleId, recipientId, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.bundle(draft!.bundleId) }),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => removeBundleItem(draft!.bundleId, itemId, sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fileKeys.bundle(draft!.bundleId) });
      refresh();
    },
  });

  const changeGate = useMutation({
    mutationFn: (gateMode: GateMode) => updateBundle(draft!.bundleId, { gateMode }, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.bundle(draft!.bundleId) }),
  });

  const send = useMutation({
    mutationFn: () => sendBundle(draft!.bundleId, undefined, sessionId),
    onSuccess: (res) => {
      toast({ title: `Sent to ${res.sent} recipient${res.sent === 1 ? "" : "s"}` });
      setOpen(false);
      refresh();
    },
    onError: (e: Error) => toast({ title: "Send failed", description: e.message, variant: "destructive" }),
  });

  if (!draft) return null;

  const itemCount = detail?.items.length ?? draft.itemCount;
  const recipientCount = detail?.recipients.length ?? 0;
  const gateMode = detail?.bundle.gateMode ?? "email_required";

  return (
    <div
      className="fixed bottom-4 right-4 z-50"
      data-testid="active-draft-chip"
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "rounded-full shadow-lg border-2 border-transparent",
              "bg-gradient-to-r from-purple-600 to-blue-600 text-white",
              "px-4 py-2 flex items-center gap-2 text-sm font-medium",
              "hover:from-purple-700 hover:to-blue-700 hover:shadow-xl transition-all",
            )}
          >
            <Paperclip className="h-4 w-4" />
            <span>{itemCount} file{itemCount === 1 ? "" : "s"}</span>
            <span className="opacity-80">· {draft.name}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="top"
          className="w-[420px] p-0"
          sideOffset={8}
        >
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">{draft.name}</h3>
              <p className="text-xs text-muted-foreground">
                {itemCount} file{itemCount === 1 ? "" : "s"} ·{" "}
                {recipientCount} recipient{recipientCount === 1 ? "" : "s"}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={async () => {
                await clearDraft();
                setOpen(false);
              }}
              aria-label="Dismiss draft"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Files */}
          <div className="max-h-44 overflow-y-auto px-3 py-2 space-y-1">
            {detail?.items.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">
                No files yet. Use "Add to share" from any file row.
              </p>
            )}
            {detail?.items.map((it) => (
              <div key={it.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50">
                <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="text-xs truncate flex-1">
                  {it.attachment?.filename ?? "(file)"}
                </span>
                {it.attachment?.fileState === "master" && (
                  <Badge variant="outline" className="text-[10px] py-0">Master</Badge>
                )}
                <Button
                  variant="ghost" size="icon"
                  className="h-6 w-6"
                  onClick={() => removeItem.mutate(it.id)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>

          {/* Recipients */}
          <div className="border-t px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Recipients
              </h4>
              <Select
                value={gateMode}
                onValueChange={(v) => changeGate.mutate(v as GateMode)}
              >
                <SelectTrigger className="h-7 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email_required">
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-3 w-3" /> Email required
                    </span>
                  </SelectItem>
                  <SelectItem value="link_only">
                    <span className="flex items-center gap-1.5">
                      <Link2 className="h-3 w-3" /> Link only
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {detail?.recipients.map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-muted/30">
                  <span className="flex-1 truncate">
                    {r.name ? `${r.name} <${r.email}>` : r.email}
                  </span>
                  {r.emailedAt && <Badge variant="outline" className="text-[10px] py-0">Sent</Badge>}
                  <Button
                    variant="ghost" size="icon" className="h-5 w-5"
                    onClick={() => removeRecipient.mutate(r.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={recipEmail}
                placeholder="recipient@example.com"
                onChange={(e) => setRecipEmail(e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <Input
                value={recipName}
                placeholder="Name (optional)"
                onChange={(e) => setRecipName(e.target.value)}
                className="h-8 text-xs w-32"
              />
              <Button
                variant="outline" size="sm" className="h-8"
                disabled={!recipEmail || addRecipient.isPending}
                onClick={() => addRecipient.mutate({ email: recipEmail, name: recipName || null })}
              >
                Add
              </Button>
            </div>
          </div>

          {/* Message */}
          <BundleMessageField bundleId={draft.bundleId} initial={detail?.bundle.message ?? ""} sessionId={sessionId} />

          {/* Actions */}
          <div className="border-t px-4 py-3 flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={async () => { await clearDraft(); setOpen(false); }}
            >
              Save draft
            </Button>
            <Button
              onClick={() => send.mutate()}
              disabled={send.isPending || itemCount === 0
                || (gateMode === "email_required" && recipientCount === 0)}
            >
              {send.isPending
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function BundleMessageField({ bundleId, initial, sessionId }: {
  bundleId: string; initial: string; sessionId: string | null;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const save = useMutation({
    mutationFn: () => updateBundle(bundleId, { message: value || null }, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: fileKeys.bundle(bundleId) }),
  });
  return (
    <div className="border-t px-4 py-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        Message (optional)
      </h4>
      <Textarea
        rows={2}
        className="text-xs resize-none"
        placeholder="Add a note for recipients…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => value !== initial && save.mutate()}
      />
    </div>
  );
}
